import { h, Fragment } from 'preact';
import { useCallback, useEffect, useState } from "preact/hooks";
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import styled from 'styled-components';
import moment from 'moment';
import { findLast, isEqual } from 'lodash';
import latlongtokm from "@riskyvaibhav/lat-long-to-km";

import { containerStyle, center, radiuses, styles, convertTime, sortGrossier } from "./util";
import { Spinner } from "./Spinner";

async function fetchGrossiers() {
  const response = await fetch(window.local_grossiers_object.api);

  try {
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    throw new Error(`Something went wrong`)
  }

  throw new Error(`Something went wrong`)
}

async function fetchGrossiersFromCorporate() {
  const response = await fetch(window.local_grossiers_object.api_corporate);

  try {
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    throw new Error(`Something went wrong`)
  }

  throw new Error(`Something went wrong`)
}

const TextButton = styled.button`
  background: transparent;
  padding: 0;
  border: none;
  outline: none;

  &:focus {
    border: none;
    outline: none;
  }
`;

const Filters = styled.section`
  margin-top: -75px;
`;

const GrossierRow = styled.div`
  border: 1px solid #979797;
`;

const SelectWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  &::after {
    content: "";
    background-image: url(${window.local_grossiers_object.down_arrow});
    width: 10px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center center;
    height: 8px;
    display: block;
    position:absolute;
    right: 15px;
    top: 0;
    bottom: 0;
    margin: auto;
    z-index: 2;
  }
`;

const InputWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  @media (max-width: 991.98px) {
    min-height: 50px;
  }

  > div {
    cursor: pointer;
    display:flex;
    align-items: center;
    font-style: italic;
    color: #C0C0C0;
    position: absolute;
    font-size: 12px;
    right: 15px;
    top: 0;
    bottom: 0;
    margin: auto;
  }
`;

const Select = styled.select`
  appearance: none;
`;



function defaultProps() {
  const urlParams = new URLSearchParams(window.location.search);
  const location = urlParams.get('location');
  const radius = urlParams.get('radius');

  if (!location && !radius) {
    return false;
  }

  return {
    location: location ? location : '',
    radius: radius ? parseInt(radius) : 0
  }
}

let hasBeenSet = defaultProps();

const Grossiers = () => {
  const [fields, setFields] = useState(hasBeenSet ? hasBeenSet : {
    location: '',
    radius: 0
  })
  const [grossiers, setGrossiers] = useState([]);
  const [visibleGrossiers, setVisibleGrossiers] = useState([]);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [lastLocation, setLastLocation] = useState({
    location: '',
    lat: 0,
    lng: 0
  });
  const [isLoading, setLoading] = useState(true);
  const [isLoadingLocation, setLoadingLocation] = useState(false);
  const [circle, setCircle] = useState(null);

  const onLoad = useCallback(async function callback(map) {
    map.setOptions({
      styles: styles,
      disableDefaultUI: true
    });
    setMap(map)
  }, [])

  const onUnmount = useCallback(function callback(map) {
    setMap(null)
  }, [])

  useEffect(() => {
    if (hasBeenSet && map !== null) {
      handleSearch().then(() => {})
      hasBeenSet = false;
    }
  }, [grossiers])

  useEffect(() => {
    setLoading(true);
    fetchGrossiers().then(async data => {
      const corporateData = await fetchGrossiersFromCorporate();

      data = [...data, ...corporateData];
      const dataWithParsedSchedule = data.map(grossier => {
        grossier.schedule = convertTime(JSON.parse(grossier.schedule))
        return grossier;
      })

      setGrossiers(dataWithParsedSchedule);
      setVisibleGrossiers(dataWithParsedSchedule);
      setLoading(hasBeenSet);
    }).catch(error => {
      setGrossiers([]);
    });
  }, []);

  useEffect(() => {
    if (markers.length > 0) {
      return;
    }

    markers.forEach(marker => marker.setMap(null));

    const markersArray = visibleGrossiers.map(grossier => {
      const marker = new google.maps.Marker({
        position: {
          lat: grossier.latitude,
          lng: grossier.longitude
        },
        map: map,
        icon: {
          url: grossier.hq === undefined ? window.local_grossiers_object.grossier_marker_path_grey : window.local_grossiers_object.grossier_marker_path,
        }
      });

      const content = `
        <div class="infoWindow">
            <h4>${grossier.title}</h4>
            <span class="d-block">${grossier.street}</span>
            <span class="d-block">${grossier.zipcode}</span>
            <span class="d-block">${grossier.city}</span>
            <a target="_blank" class="link-primary" href="${grossier.url}">Naar website</a>
        </div>
      `;

      const infoWindow = new google.maps.InfoWindow({
        content: content,
      });

      marker['grossier'] = grossier;

      marker.addListener("click", function (e) {
        infoWindow.open(map, marker);
      });

      marker.addListener("mouseenter", function (e) {
        infoWindow.open(map, marker);
      });

      return marker;
    });

    setMarkers(markersArray);

    return () => {
      markers.forEach(marker => marker.setMap(null));
      setMarkers([]);
    }
  }, [visibleGrossiers]);

  useEffect(() => {
    if (!map || lastLocation.location === '') return;
    circle && circle.setMap(null);
    setCircle(null);

    const mapsCircle = new google.maps.Circle({
      strokeColor: "#234187",
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: "#1762AB",
      fillOpacity: 0.35,
      map: map,
      center: {
        lat: lastLocation.lat,
        lng: lastLocation.lng,
      },
      radius: parseInt(fields.radius) * 1000
    });

    setCircle(mapsCircle)
    map.setCenter({
      lat: lastLocation.lat,
      lng: lastLocation.lng
    });

    if (parseInt(fields.radius) === 100) {
      map.setZoom(8);
    } else if (parseInt(fields.radius) >= 50) {
      map.setZoom(9)
    }

    return () => {
      circle && circle.setMap(null);
      setCircle(null);
    }
  }, [map, lastLocation, visibleGrossiers]);

  function filterGrossiers(grossier) {
    if (!fields.radius || grossier.distance === undefined) {
      return true;
    }

    return parseInt(fields.radius) >= grossier.distance;
  }

  function handleChange(e) {
    if (e.target.name === "location" && e.target.value === "") {
      setFields({
        location: "", radius: 0
      });

      setVisibleGrossiers(grossiers);
    } else {
      setFields({
        ...fields,
        [e.target.name]: e.target.value
      })
    }
  }

  function fetchGeolocation(location) {
    let args = {
      address: location
    };

    if (typeof location === 'object') {
      args = {
        location: location
      }
    }

    const service = new window.google.maps.Geocoder();

    return new Promise((resolve, reject) => {
      service.geocode(args, (response, status) => {
        if (status === "OK") {
          resolve(response);
        } else {
          reject(response);
        }
      });
    });
  }

  async function handleSearch(e = false) {
    console.log("Submitting...")
    if (e) {
      e.preventDefault();
    }

    if (fields.radius && fields.location) {
      if (lastLocation.location === fields.location) {
        setVisibleGrossiers(grossiers.filter(filterGrossiers));
        return;
      }

      setLoading(true);
      const mapsData = await fetchGeolocation(`${fields.location}, Nederland`);
      const results = mapsData[0];

      grossiers.forEach((grossier, index) => {
        const km = latlongtokm(
          grossier.latitude,
          grossier.longitude,
          results.geometry.location.lat(),
          results.geometry.location.lng(),
        );
        grossiers[index].distance = Math.round(km);
      });

      setGrossiers([ ...grossiers ]);
      setVisibleGrossiers(grossiers.filter(filterGrossiers));
      setLastLocation({
        location: fields.location,
        lat: results.geometry.location.lat(),
        lng: results.geometry.location.lng(),
      });
      setLoading(false);
    }
  }

  function handleLocation() {
    if (!navigator.geolocation) {
      alert('Deze functie is niet beschikbaar in uw browser.')
    } else {
      setLoadingLocation(true);
      navigator.geolocation.getCurrentPosition((position) => {
        fetchGeolocation({ lat: position.coords.latitude, lng: position.coords.longitude }).then(mapsData => {
          setLoadingLocation(false);
          const results = mapsData[0];

          setFields({
            ...fields,
            location: results.formatted_address
          })
        })
      }, () => {
        setLoadingLocation(false);
        alert("Kon uw locatie niet ophalen.")
      })
    }
  }

  function resetFilter() {
    window.location.href = window.location.origin + window.location.pathname;
  }

  visibleGrossiers.sort(sortGrossier);

  return (
    <div className="grossiers-page">
      <LoadScript
        googleMapsApiKey="AIzaSyBzq4Ixboz3rLpxFuYL3Dia1OtbPtruQg0"
      >
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={10}
          onLoad={onLoad}
          id="grossiersMap"
          onUnmount={onUnmount}
        />
      </LoadScript>

      <Filters className="container mb-5">
        <div className="row justify-content-center">
          <div className="col-xl-9 col-lg-11">
            <div className="bg-gradient py-4 px-4">

              <div className="d-flex justify-content-between flex-lg-row flex-column mb-3">
                <h1 className="text-white font-size-h2 mb-lg-0 mb-2">Vind een grossier in de buurt</h1>

                <TextButton onClick={resetFilter} className="with-arrow-white">
                  Alle vestigingen
                </TextButton>
              </div>

              <form className="row" method="get" onSubmit={handleSearch}>
                <div className="col-lg-5 mb-lg-0 mb-3">
                  <InputWrapper>
                    <input className="w-100 h-100" type="text" name="location" onInput={handleChange} value={fields.location} placeholder="Locatie" required />
                    {fields.location === "" && (
                      <Fragment>
                        {isLoadingLocation ? (
                          <Spinner />
                        ) : (
                          <div onClick={handleLocation}>
                            Gebruik uw locatie <img className="ml-2" src={window.local_grossiers_object.cursor} alt="" />
                          </div>
                        )}
                      </Fragment>
                    )}
                  </InputWrapper>
                </div>
                <div className="col-lg-3 mb-lg-0 mb-3">
                  <SelectWrapper>
                    <Select className="w-100 h-100" name="radius" onChange={handleChange} value={fields.radius}>
                      {radiuses.map(radius => (
                        <option value={radius.value}>{radius.text}</option>
                      ))}
                    </Select>
                  </SelectWrapper>

                </div>

                <div className="col-lg-4">
                  <button className="button-outline font-size-small w-100 h-100 font-size-h5" type="submit">
                    Zoek een vestiging
                    <svg className="ml-3" width="16" height="16" viewBox="0 0 16 16" fill="#fff">
                      <defs>
                        <clipPath id="a">
                          <path d="M0,0H16V16H0Z" fill="none" />
                        </clipPath>
                      </defs>
                      <path d="M0,0H16V16H0Z" fill="none" />
                      <g clipPath="url(#a)">
                        <path
                          d="M14.716,16a1.284,1.284,0,0,1-.931-.392L9.259,11.2a6.239,6.239,0,0,1-6.667-.271A5.962,5.962,0,0,1,.085,7.012,5.888,5.888,0,0,1,1.129,2.541,6.219,6.219,0,0,1,9.684,1.106a5.964,5.964,0,0,1,2.507,3.918,5.886,5.886,0,0,1-1.045,4.47L15.6,13.828l.047.046a1.243,1.243,0,0,1-.047,1.78A1.3,1.3,0,0,1,14.716,16ZM6.131,2.145a3.972,3.972,0,0,0-2.8,1.138A3.817,3.817,0,0,0,2.174,6.026,3.926,3.926,0,0,0,6.134,9.905a3.978,3.978,0,0,0,2.8-1.141A3.818,3.818,0,0,0,10.09,6.017,3.926,3.926,0,0,0,6.131,2.145Z" />
                      </g>
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Filters>

      <section className="container">
        {isLoading ? (
          <div className="w-100 d-flex justify-content-center">
            <Spinner />
          </div>
        ) : (
          <Fragment>
            <h2 className="text-primary-light mb-4">{visibleGrossiers.length} {visibleGrossiers.length === 1 ? 'zoekresultaat': 'zoekresultaten'}</h2>
            {visibleGrossiers.map(grossier => <Row key={grossier.title} grossier={grossier} />)}
          </Fragment>
        )}
      </section>
    </div>
  );
};

function Row({ grossier }) {
  return (
    <GrossierRow className="py-4 px-lg-5 px-4 mb-4">
      <div className="row">
        <div className="col-lg-12">
          <h3 className="text-grey mb-4">
            {grossier.title}

            {grossier.distance !== undefined && (
              <span> - {grossier.distance} km</span>
            )}
          </h3>
        </div>

        <div className="col-lg-4 mb-lg-0 mb-3">
          <div>{grossier.street}</div>
          <div>{grossier.zipcode}</div>
          <div>{grossier.city}</div>
          <a
            className="link-primary"
            href={`https://google.com/maps/dir/?api=1&travelmode=driving&destination=${grossier.street},${grossier.zipcode} ${grossier.city}`}
            target="_blank"
            rel="noreferrer noopener"
          >
            Route plannen
          </a>
        </div>

        <div className="col-lg-5 mb-lg-0 mb-3">
          <strong className="text-grey font-weight-500">Openingstijden</strong>
          <div>
            {grossier.schedule.map((day, key) => (
              <div key={key} className="row">
                <div className="col-6">{day.name}</div>
                <div className="col-6">
                  {day.value.length === 0 ? "Gesloten" : day.value.map(time => <div>{time.start} - {time.end}</div>)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-3 pt-4">
          <a href={grossier.url} target="_blank" rel="noreferrer noopener" className="text-white bg-primary-light rounded px-4 py-3">
            Naar website
          </a>
        </div>
      </div>
    </GrossierRow>
  )
}

export default Grossiers;
